/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 18th 2022, 9:37:00 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useGlobalScope } from "core/providers";
import { resolveURLName } from "core/routes/utils";
import { APISERVICEPATHS } from "lib/api";
import { ListEditorProvider, ListEditorContext } from "lib/providers";
import { WorkerAccount, Team } from "lib/schemas";
import { DataTable, HeadCell, OSAvatar, AlertSnackBar } from "ui-widgets";
import { ProgressWrapper, EmptyListWrapper, APIErrorWrapper, OSIconButton } from "ui-widgets";
import { ChangeWorkerTypeButton } from "workers/components/ChangeWorkerType";
import { RemoveWorkerButton } from "workers/components/RemoveWorker";

interface WorkersListProps {
  selectedTeam: Team;
}

const headCells: HeadCell<WorkerAccount, "full_name">[] = [
  {
    id: "full_name",
    numeric: false,
    disablePadding: true,
    label: "Full Name",
  },
  {
    id: "username",
    numeric: false,
    disablePadding: true,
    label: "Username",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: true,
    label: "Email",
  },
];

export const WorkersList = ({ selectedTeam }: WorkersListProps) => {
  const { globalState } = useGlobalScope();
  const navigate = useNavigate();

  const [workerDeletionState, setWorkerDeletionState] = React.useState({
    workerName: null,
    showAlert: false,
  });
  const deleteWorkerSuccessMessage = `${workerDeletionState.workerName} has been deleted and no longer has access to your organization's portals`;

  return (
    <ListEditorProvider<WorkerAccount, false> apiServicePath={APISERVICEPATHS.workers} uniqueIdentifier="id">
      <ListEditorContext.Consumer>
        {({
          isFetchingItems,
          fetchItemsHasError,
          fetchItemsStatusCode,
          itemsListState: { savedItems: workers },
          updateList,
          removeItem,
        }) => {
          const filteredWorkers = selectedTeam
            ? workers.filter((worker) => selectedTeam.members.includes(worker.id))
            : workers;

          return (
            <ProgressWrapper showLoader={isFetchingItems}>
              <APIErrorWrapper isAPIError={fetchItemsHasError} httpResponseStatusCode={fetchItemsStatusCode}>
                <EmptyListWrapper items={workers} noDataMessage="No Workers Found">
                  <AlertSnackBar
                    open={workerDeletionState.showAlert}
                    message={deleteWorkerSuccessMessage}
                    handleClose={() => setWorkerDeletionState((prev) => ({ ...prev, showAlert: false }))}
                    severity="success"
                  />
                  <DataTable<WorkerAccount, "full_name">
                    title={`${globalState.selectedOrganization?.name ?? ""} Workers`}
                    headCells={headCells}
                    defaultOrderBy="username"
                    initialRowsPerPage={10}
                    dataSource={filteredWorkers}
                    uniqueKey="id"
                    noDataMessage={
                      globalState.selectedOrganization
                        ? `No Users Found for ${globalState.selectedOrganization.name}`
                        : "No Users Found"
                    }
                    searchKeys={["profile_name", "username", "email"]}
                    unit="User"
                    titleKey="username"
                    renderRowActions={(worker) => (
                      <>
                        <OSIconButton
                          onClick={() => navigate(resolveURLName("viewWorker", { workerId: worker.id }))}
                          icon={<VisibilityIcon color="primary" />}
                          toolTipText={<Typography>View {worker.first_name}'s profile</Typography>}
                        />
                        <ChangeWorkerTypeButton worker={worker} handleUpdatedWorkerFromAPI={updateList} />
                        <RemoveWorkerButton
                          worker={worker}
                          handleUpdatedWorkerFromAPI={(worker) => {
                            removeItem(worker.id);
                            setWorkerDeletionState({ workerName: worker.profile_name, showAlert: true });
                          }}
                        />
                      </>
                    )}
                    listViewMode="table"
                    renderItemAvatar={(worker) => (
                      <OSAvatar name={worker.username} imageSrc={worker.registrationprofile?.image} />
                    )}
                    cellRenderProps={{
                      full_name: (user) => (
                        <>
                          {user.registrationprofile
                            ? `${user.registrationprofile.firstname} ${user.registrationprofile?.surname}`
                            : "N/A"}
                        </>
                      ),
                      email: (user) => <a href={`mailto:${user.email}`}>{user.email}</a>,
                    }}
                  />
                </EmptyListWrapper>
              </APIErrorWrapper>
            </ProgressWrapper>
          );
        }}
      </ListEditorContext.Consumer>
    </ListEditorProvider>
  );
};
